import type { ImageLoader } from 'next/image';

import type { TMedia } from '~constants/media';
import { MediaTypes } from '~constants/media';

import { loaders } from './loaders';
import { buildFullUrl, isImgType, selectBestVariant } from './utils';

export const getPublic = (itemArr: TMedia[]) => {
	const image = itemArr.find((item) => isImgType(item, MediaTypes.cfpublic));
	if (image) {
		return {
			type: MediaTypes.cfpublic,
			src: image.reference,
			loader: loaders[MediaTypes.cfpublic],
		};
	}
	return null;
};

export const getCf = (
	itemArr: TMedia[],
	size: {
		width: number | `${number}` | undefined;
		height: number | `${number}` | undefined;
	},
	showPreview = false
) => {
	const image = itemArr.find((item) => isImgType(item, MediaTypes.cf));
	if (image) {
		return {
			type: MediaTypes.cf,
			src: selectBestVariant(
				showPreview
					? image.previewLinks ?? image.sizeLinks
					: image.sizeLinks ?? image.previewLinks,
				size.width,
				size.height
			),
			previewSrc: selectBestVariant(
				image.previewLinks ?? image.sizeLinks,
				size.width,
				size.height
			),
			loader: loaders[MediaTypes.cf],
		};
	}
	return null;
};
export const getVideoCfPreviewImage = (itemArr: TMedia[]) => {
	const image = itemArr.find((item) =>
		isImgType(item, MediaTypes.videoPreviewCf)
	);
	if (image) {
		return {
			type: MediaTypes.videoCf,
			src: image.reference,
			loader: loaders[MediaTypes.videoCf],
		};
	}
	return null;
};
export const getVideoCfPreview = (
	itemArr: TMedia[],
	size: {
		width: number | `${number}` | undefined;
		height: number | `${number}` | undefined;
	}
) => {
	const image = itemArr.find((item) => isImgType(item, MediaTypes.videoCf));
	if (image) {
		return {
			type: MediaTypes.videoCf,
			src: pickImageByType(image.previewLinks, size).src,
			loader: loaders[MediaTypes.videoCf],
		};
	}
	return null;
};

export const getDefault = (itemArr: TMedia[]) => {
	const image = itemArr.find((item) => isImgType(item, MediaTypes.default));

	if (image) {
		if (image.reference) {
			return {
				type: MediaTypes.default,
				src: buildFullUrl(image.reference),
				loader: undefined,
			};
		}
		throw new Error('No reference field in default image.');
	}
	return null;
};

const strategies = [
	getPublic,
	getVideoCfPreviewImage,
	getVideoCfPreview,
	getCf,
	getDefault,
];

export function pickImageByType(
	itemArr: TMedia[],
	size: {
		width: number | `${number}` | undefined;
		height: number | `${number}` | undefined;
	},
	fallback = '',
	showPreview = false
): {
	src: string;
	loader: ImageLoader | undefined;
	type: MediaTypes;
	previewSrc?: string;
} {
	for (const strategy of strategies) {
		const image = strategy(itemArr, size, showPreview);
		if (image) {
			return image;
		}
	}

	return {
		type: MediaTypes.default,
		src: buildFullUrl(fallback),
		loader: undefined,
	};
}
